<template>
  <section class="reports-filter-toolbar app-filter-toolbar" :aria-label="filterToolbarText.ariaLabel">
    <div
      class="app-filter-toolbar-row reports-toolbar-row"
      :class="{ 'reports-toolbar-row--open': showFilters }"
    >
      <div class="reports-toolbar-left">
        <multiselect
          v-for="filter in quickFilters"
          :key="filter.key"
          v-model="filters[filter.key]"
          :options="options[filter.key]"
          track-by="value"
          label="label"
          :multiple="!isSingleFilter(filter.key)"
          :close-on-select="shouldClosePanelOnSelect(filter.key)"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :max-height="220"
          :placeholder="filter.placeholder"
          :class="[
            'reports-toolbar-inline-select app-filter-select app-select app-select--filters',
            {
              'reports-toolbar-inline-select--customer': isCustomerFilter(filter.key),
              'reports-toolbar-inline-select--compact': isPeriodOrValueFilter(filter.key)
            }
          ]"
          @select="onSelect(filter.key, $event)"
          @remove="onRemove(filter.key)"
        >
          <template #singleLabel="{ option }">
            <span>
              {{ singleSelectionLabel(filter.key, option) }}
            </span>
          </template>
          <template #selection="{ values, isOpen }">
            <span v-if="!isOpen && !isSingleFilter(filter.key)" class="multiselect__single">
              {{ selectionLabel(filter.key, values) }}
            </span>
          </template>
          <template #noResult>
            <span>{{ filterToolbarText.noOptions }}</span>
          </template>
          <template #noOptions>
            <span>{{ filterToolbarText.noOptions }}</span>
          </template>
        </multiselect>
      </div>

      <div class="app-filter-toolbar-right">
        <AppFilterDateRange
          v-model="dateRangeValue"
          class="app-filter-date-inline"
          label=""
          :show-input="showDateInput"
          :show-clear-option="false"
          :presets="dateRangePresets"
          :initial-preset-key="initialDatePresetKey"
          :clear-preset-on-manual-select="true"
          @change="onDateRangeChange"
        />

        <div ref="sortMenu" class="reports-toolbar-sort">
          <button
            type="button"
            class="app-filter-icon-btn"
            :class="{ active: isSortActive }"
            :title="sortButtonTitle"
            :aria-expanded="showSortMenu"
            @click.stop="toggleSortMenu"
          >
            <i class="fa" :class="sortButtonIconClass" aria-hidden="true"></i>
          </button>

          <div v-if="showSortMenu" class="reports-sort-dropdown" @click.stop>
            <button
              v-for="option in sortDirectionOptions"
              :key="option.value"
              type="button"
              class="reports-sort-dropdown-option"
              :class="{ 'reports-sort-dropdown-option--active': sortDirection === option.value }"
              @click="selectSortDirection(option.value)"
            >
              <i class="fa" :class="option.icon" aria-hidden="true"></i>
              <span>{{ option.label }}</span>
            </button>
          </div>
        </div>

        <button
          type="button"
          class="app-filter-icon-btn"
          :class="{ active: showFilters }"
          :title="showFilters ? filterToolbarText.toggleHideTitle : filterToolbarText.toggleShowTitle"
          @click="toggleFilters"
        >
          <i class="fa fa-filter" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-filter-icon-btn sales-toolbar-search-btn"
          :title="filterToolbarText.applyTitle"
          @click="emitApply"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition name="filters-expand" @after-leave="handleFiltersAfterLeave">
      <div v-show="showFilters" class="app-filter-panel reports-filter-panel">
        <div class="reports-filters-grid reports-filters-grid--first-row">
          <div
            v-for="filter in firstRowFilterConfigs"
            :key="filter.key"
            class="app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="!isSingleFilter(filter.key)"
              :close-on-select="shouldClosePanelOnSelect(filter.key)"
              :clear-on-select="false"
              :preserve-search="shouldPreservePanelSearch(filter.key)"
              :show-labels="false"
              :searchable="isPanelSelectorSearchable(filter.key)"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              :class="[
                'app-filter-select app-select app-select--filters',
                { 'reports-filter-select--left': isLeftAlignedSingleFilter(filter.key) }
              ]"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
            >
              <template #singleLabel="{ option }">
                {{ panelSingleSelectionLabel(filter.key, option) }}
              </template>
              <template #selection="{ values, isOpen }">
                <span
                  v-if="!isOpen && !isSingleFilter(filter.key)"
                  class="multiselect__single"
                >
                  {{ buildFilterSelectionLabel(filter.key, values) }}
                </span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="reports-filters-grid reports-filters-grid--second-row">
          <div
            v-for="filter in secondRowFilterConfigs"
            :key="filter.key"
            class="app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="!isSingleFilter(filter.key)"
              :close-on-select="shouldClosePanelOnSelect(filter.key)"
              :clear-on-select="false"
              :preserve-search="shouldPreservePanelSearch(filter.key)"
              :show-labels="false"
              :searchable="isPanelSelectorSearchable(filter.key)"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              :class="[
                'app-filter-select app-select app-select--filters',
                { 'reports-filter-select--left': isLeftAlignedSingleFilter(filter.key) }
              ]"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
            >
              <template #singleLabel="{ option }">
                {{ panelSingleSelectionLabel(filter.key, option) }}
              </template>
              <template #selection="{ values, isOpen }">
                <span
                  v-if="!isOpen && !isSingleFilter(filter.key)"
                  class="multiselect__single"
                >
                  {{ buildFilterSelectionLabel(filter.key, values) }}
                </span>
              </template>
            </multiselect>
          </div>
        </div>

        <div
          v-if="thirdRowFilterConfigs.length > 0"
          class="reports-filters-grid reports-filters-grid--third-row"
        >
          <div
            v-for="filter in thirdRowFilterConfigs"
            :key="filter.key"
            class="app-filter-field"
          >
            <div
              v-if="shouldShowCompositeExchangeRateInput(filter.key)"
              class="reports-filter-composite-label-row"
            >
              <span>{{ filter.label }}</span>
              <span>{{ filterToolbarText?.exchangeRateLabel || 'Tipo de cambio' }}</span>
            </div>
            <span v-else>{{ filter.label }}</span>
            <div
              class="reports-filter-field-control"
              :class="{ 'reports-filter-field-control--composite': shouldShowCompositeExchangeRateInput(filter.key) }"
            >
              <multiselect
                v-model="filters[filter.key]"
                :options="options[filter.key]"
                track-by="value"
                label="label"
                :multiple="!isSingleFilter(filter.key)"
                :close-on-select="shouldClosePanelOnSelect(filter.key)"
                :clear-on-select="false"
                :preserve-search="shouldPreservePanelSearch(filter.key)"
                :show-labels="false"
                :searchable="isPanelSelectorSearchable(filter.key)"
                :allow-empty="true"
                :max-height="220"
                :placeholder="filter.placeholder"
                :class="[
                  'app-filter-select app-select app-select--filters',
                  { 'reports-filter-select--left': isLeftAlignedSingleFilter(filter.key) }
                ]"
                @select="onSelect(filter.key, $event)"
                @remove="onRemove(filter.key)"
              >
                <template #singleLabel="{ option }">
                  {{ panelSingleSelectionLabel(filter.key, option) }}
                </template>
                <template #selection="{ values, isOpen }">
                  <span
                    v-if="!isOpen && !isSingleFilter(filter.key)"
                    class="multiselect__single"
                  >
                    {{ buildFilterSelectionLabel(filter.key, values) }}
                  </span>
                </template>
              </multiselect>

              <input
                v-if="shouldShowCompositeExchangeRateInput(filter.key)"
                type="number"
                min="0.0001"
                step="0.0001"
                class="reports-filter-composite-exchange-rate-input"
                :value="compositeExchangeRateInputValue(filter.key)"
                :placeholder="filterToolbarText?.exchangeRatePlaceholder || 'Tipo de cambio'"
                :title="filterToolbarText?.exchangeRatePlaceholder || 'Tipo de cambio'"
                @input="onCompositeExchangeRateInput(filter.key, $event?.target?.value)"
                @blur="onCompositeExchangeRateBlur(filter.key)"
              />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import AppFilterDateRange from '@/components/common/AppFilterDateRange.vue';
import {
  shouldEmitToolbarDateChange,
} from '@/composables/shared/useFilterToolbarShared';
import { REPORTS_SALES_TEXT, requireReportsSalesTextValue } from '@/composables/reports/sales/useReportsSalesPresentation';
import {
  DEFAULT_REPORTS_TOOLBAR_KEY,
} from '@/composables/reports/general/toolbar/useReportsFilterToolbarHelpers';
import {
  createReportsFiltersToolbarState,
  reportsFiltersToolbarComputed,
  reportsFiltersToolbarMethods,
} from '@/composables/reports/general/toolbar/useReportsFilterToolbarController';

const DEFAULT_REPORT_KEY = DEFAULT_REPORTS_TOOLBAR_KEY;

export default {
  name: 'ReportsFilterToolbar',
  components: {
    AppFilterDateRange,
  },
  props: {
    reportKey: {
      type: String,
      default: DEFAULT_REPORT_KEY,
    },
    reportTab: {
      type: String,
      default: '',
    },
    requestParams: {
      type: Object,
      default: () => ({}),
    },
    selectorsFetcher: {
      type: Function,
      default: null,
    },
    localSelectorDefinitions: {
      type: Array,
      default: () => [],
    },
    localSelectorOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['apply', 'filters-change'],
  computed: {
    filterToolbarText() {
      const text = REPORTS_SALES_TEXT.filterToolbar;
      requireReportsSalesTextValue(text.ariaLabel, 'reports.sales.filterToolbar.ariaLabel');
      requireReportsSalesTextValue(text.noOptions, 'reports.sales.filterToolbar.noOptions');
      requireReportsSalesTextValue(text.toggleHideTitle, 'reports.sales.filterToolbar.toggleHideTitle');
      requireReportsSalesTextValue(text.toggleShowTitle, 'reports.sales.filterToolbar.toggleShowTitle');
      requireReportsSalesTextValue(text.applyTitle, 'reports.sales.filterToolbar.applyTitle');
      requireReportsSalesTextValue(text.allLabel, 'reports.sales.filterToolbar.allLabel');
      requireReportsSalesTextValue(text.selectedLabelSuffix, 'reports.sales.filterToolbar.selectedLabelSuffix');
      requireReportsSalesTextValue(text.columnSingular, 'reports.sales.filterToolbar.columnSingular');
      requireReportsSalesTextValue(text.columnPlural, 'reports.sales.filterToolbar.columnPlural');
      requireReportsSalesTextValue(text.defaultPrefix, 'reports.sales.filterToolbar.defaultPrefix');
      requireReportsSalesTextValue(text?.sort?.buttonPrefix, 'reports.sales.filterToolbar.sort.buttonPrefix');
      requireReportsSalesTextValue(text?.sort?.options?.none, 'reports.sales.filterToolbar.sort.options.none');
      requireReportsSalesTextValue(text?.sort?.options?.asc, 'reports.sales.filterToolbar.sort.options.asc');
      requireReportsSalesTextValue(text?.sort?.options?.desc, 'reports.sales.filterToolbar.sort.options.desc');

      if (!Array.isArray(text.dateRangePresets) || text.dateRangePresets.length === 0) {
        throw new Error('Missing reports sales text value: reports.sales.filterToolbar.dateRangePresets');
      }

      return text;
    },
    ...reportsFiltersToolbarComputed,
  },
  watch: {
    reportKey() {
      this.handleReportScopeChange();
    },
    reportTab() {
      this.handleReportScopeChange();
    },
  },
  data() {
    return createReportsFiltersToolbarState();
  },
  async mounted() {
    document.addEventListener('click', this.handleSortMenuOutsideClick);
    await this.loadSelectorOptions();
    this.emitFilters('init');
    this.isToolbarBootstrapped = true;
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleSortMenuOutsideClick);
  },
  methods: {
    ...reportsFiltersToolbarMethods,
    onDateRangeChange(payload) {
      const nextValue = Array.isArray(payload?.value) ? payload.value : null;
      const shouldEmit = shouldEmitToolbarDateChange(payload, nextValue);
      const source = String(payload?.source || '').trim().toLowerCase();

      this.dateRangeValue = nextValue;

      if (!this.isToolbarBootstrapped && source === 'preset') {
        return;
      }

      if (shouldEmit) {
        this.$emit('apply', this.emitFilters('date'));
      }
    },
  },
};
</script>
